import React, { memo, useMemo } from 'react';
import {
  WarehousePosition,
  groupPositionsByStreet,
  getStatusColorClass,
} from '../helpers/warehouseMap';

interface WarehouseMapProps {
  positions: WarehousePosition[];
  selectedPositionId?: string;
  onSelectPosition: (position: WarehousePosition) => void;
}

const WarehouseMap: React.FC<WarehouseMapProps> = ({
  positions,
  selectedPositionId,
  onSelectPosition,
}) => {
  const groupedStreets = useMemo(() => groupPositionsByStreet(positions), [positions]);

  if (groupedStreets.length === 0) {
    return (
      <div className="wms-wh-empty">
        Nenhuma posição encontrada para os filtros aplicados.
      </div>
    );
  }

  return (
    <div className="wms-wh-map-container">
      {groupedStreets.map((street) => {
        const allPositions: WarehousePosition[] = street.colunas.flatMap((c) => c.posicoes);
        const allColumns = [...new Set(allPositions.map((p) => p.coluna))].sort((a, b) => a - b);
        const allLevels = [...new Set(allPositions.map((p) => p.nivel))].sort((a, b) => b - a);

        const posMap = new Map<string, WarehousePosition>();
        for (const pos of allPositions) {
          posMap.set(`${pos.nivel}-${pos.coluna}`, pos);
        }

        return (
          <section key={street.rua} className="wms-wh-street-block">
            <div className="wms-wh-street-grid-scroll">
              <table className="wms-wh-street-grid">
                <thead>
                  <tr>
                    <th className="wms-wh-street-name-cell">
                      <div className="wms-wh-sname">Rua {street.rua}</div>
                      <div className="wms-wh-scols">{allColumns.length} colunas</div>
                    </th>
                    {allColumns.map((col) => (
                      <th key={col} className="wms-wh-col-header">
                        {String(col).padStart(2, '0')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allLevels.map((nivel) => (
                    <tr key={nivel}>
                      <td className="wms-wh-nivel-label">
                        Nível {String(nivel).padStart(2, '0')}
                      </td>
                      {allColumns.map((col) => {
                        const pos = posMap.get(`${nivel}-${col}`);
                        if (!pos) {
                          return (
                            <td key={col} className="wms-wh-cell-empty">
                              <span className="wms-wh-empty-dash">—</span>
                            </td>
                          );
                        }
                        const isSelected = selectedPositionId === pos.id;
                        return (
                          <td key={col} className="wms-wh-cell">
                            <button
                              type="button"
                              className={`wms-wh-pos-btn ${getStatusColorClass(pos.status)}${isSelected ? ' wms-wh-pos-selected' : ''}`}
                              onClick={() => onSelectPosition(pos)}
                              title={`${pos.codigoCompleto || pos.id} · ${pos.tipo} · ${pos.capacidade.toLocaleString('pt-BR')} kg`}
                            >
                              {pos.codigoCompleto || `${String(nivel).padStart(2, '0')}-${String(col).padStart(2, '0')}`}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}

      <div className="wms-wh-legend-bottom">
        <span className="wms-wh-legend-item"><i className="wms-wh-dot wms-wh-dot-livre" />Livre</span>
        <span className="wms-wh-legend-item"><i className="wms-wh-dot wms-wh-dot-ocupado" />Ocupado</span>
        <span className="wms-wh-legend-item"><i className="wms-wh-dot wms-wh-dot-bloqueado" />Bloqueado</span>
        <span className="wms-wh-legend-item"><i className="wms-wh-dot wms-wh-dot-vazio" />Vazio</span>
      </div>
    </div>
  );
};

export default memo(WarehouseMap);
